import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { TokenPayload } from '@recurly/recurly-js';
import { CountrySelect, CreditCardForm, useRecurly } from '@fff-web/fff-payments';
import type { IFieldState } from '@fff-web/fff-payments';
import { Button } from '@fff-web/fff-ui-shared';
import { useViewSize } from '@fff-web/fff-utilities';
import ErrorBanner from '../../ErrorBanner/ErrorBanner';
import * as styles from './GiftCardPayForm.module.css';

const CTA_PAY_NOW = 'PAY NOW';
const MISSING_FIELDS_ERROR_MSG = 'Please fill out the missing fields.';

type GiftCardPayFormProps = {
  onSubmit: (token: TokenPayload) => void;
  isDisabled: boolean;
  payFieldsState: {
    fields: Record<string, IFieldState>;
    hasErrors: boolean;
    onResponse: (err: Error, token: TokenPayload) => void;
  };
};

const GiftCardPayForm = ({ onSubmit, isDisabled, payFieldsState }: GiftCardPayFormProps) => {
  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const { isMobile } = useViewSize();
  const recurly = useRecurly();
  const { fields: fieldsState, hasErrors, onResponse } = payFieldsState;
  const [creditCardValidationError, setCreditCardValidationError] = useState(false);

  useEffect(() => {
    if (!hasErrors && creditCardValidationError) {
      setCreditCardValidationError(false);
    }
  }, [hasErrors]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSendingRequest(true);
    recurly.token(formRef.current!, (err, token) => {
      onResponse(err as Error, token);
      if (!err) {
        onSubmit(token);
      } else if (err.code === 'validation' || hasErrors) {
        setCreditCardValidationError(true);
      }
      setIsSendingRequest(false);
    });
  }

  return (
    <form onSubmit={handleSubmit} ref={formRef} className={styles.gcpfContainer}>
      {creditCardValidationError && <ErrorBanner message={MISSING_FIELDS_ERROR_MSG} />}
      <CreditCardForm
        name={fieldsState.first_name}
        lastName={fieldsState.last_name}
        number={fieldsState.number}
        month={fieldsState.month}
        year={fieldsState.year}
        cvv={fieldsState.cvv}
        postalCode={fieldsState.postal_code}
      />
      <div className={styles.gcpfRow}>
        <CountrySelect fieldState={fieldsState.country} />
      </div>
      <div className={styles.gcpfWarning}>
        By clicking &ldquo;PAY NOW&rdquo; you confirm that you accept the{' '}
        <a
          className={styles.gcpfTermsLink}
          href="https://legal.fabfitfun.com/#terms-of-use"
          target="_blank"
          rel="noreferrer"
        >
          Terms of Use and Sale
        </a>{' '}
        and{' '}
        <a
          className={styles.gcpfTermsLink}
          href="https://legal.fabfitfun.com/#gift-card-terms"
          target="_blank"
          rel="noreferrer"
        >
          Gift Card Terms
        </a>{' '}
        and that your credit or debit card will be immediately charged. The gift card will be
        delivered to the recipient immediately by email.
      </div>
      <Button
        type="submit"
        styles={{ alignSelf: 'center' }}
        disabled={isDisabled || isSendingRequest}
        loading={isSendingRequest}
        containerFullWidth={isMobile}
      >
        {CTA_PAY_NOW}
      </Button>
    </form>
  );
};

GiftCardPayForm.defaultProps = {
  onSubmit: () => null,
  isDisabled: false,
};

export default GiftCardPayForm;
